// Shared helpers for the placing screens.

/** Badge colour for each placing status (submission, lead, quote request, quote). */
export const STATUS_COLOURS = {
  draft: "gray",
  shared: "blue",
  selected: "gray",
  sent: "blue",
  declined: "red",
  quoted: "purple",
  nominated: "green",
  ntu: "orange",
  withdrawn: "gray",
  interested: "teal",
  offered: "purple",
  signed: "green",
};

/** Labels for statuses whose code doesn't read well on a badge. */
export const STATUS_LABELS = { ntu: "Not taken up", signed: "Signed" };

/** The placement has been closed to further lines and signed down. */
export const isSignedDown = (placement) => placement?.status === "signed";

/** A carrier's role on a line request (receiver.roleCode) or offer (sender.roleCode). */
export const LINE_ROLES = {
  lead: { label: "Lead", colour: "blue", variant: "solid" },
  follower: { label: "Follower", colour: "gray", variant: "subtle" },
};

/** An offer's line type: conditional when it has conditions. */
export const LINE_TYPES = {
  conditional_line: { label: "Conditional", colour: "orange" },
  unconditional_line: { label: "Unconditional", colour: "green" },
};

/**
 * An offer's line details, whether it comes from the lines view
 * (lineType, lineToStand, conditions at the top level) or is the stored
 * offer record (placingTransactionFunctionCode, participation, and
 * lineConditionsDescription).
 */
export const readOffer = (offer) => {
  const p = offer?.participation || {};
  const conditions = offer?.conditions ?? offer?.lineConditionsDescription ?? "";
  return {
    writtenShare: offer?.writtenShare ?? p.writtenShare,
    signedShare: offer?.signedShare ?? p.signedShare,
    lineToStand: !!(offer?.lineToStand ?? p.lineToStandIndicator),
    conditions,
    lineType:
      offer?.lineType ||
      (["conditional_line", "unconditional_line"].includes(offer?.placingTransactionFunctionCode)
        ? offer.placingTransactionFunctionCode
        : conditions
        ? "conditional_line"
        : "unconditional_line"),
  };
};

/**
 * A carrier's response on a party entry (responseCode), as recorded by the
 * API: at build declare_interest / decline_to_participate, at quotation
 * quotation / decline_to_quote.
 */
export const RESPONSES = {
  declare_interest: { label: "Interested", colour: "teal" },
  decline_to_participate: { label: "Declined to participate", colour: "red" },
  quotation: { label: "Quoted", colour: "purple" },
  decline_to_quote: { label: "Declined to quote", colour: "red" },
};

const isDeclined = (entry) => entry?.status === "declined" || entry?.responseCode === "decline_to_participate";

/** The party has declared interest (entry status interested) and not declined since. */
export const isInterested = (entry) =>
  !isDeclined(entry) && (entry?.status === "interested" || entry?.responseCode === "declare_interest");

/** Whether a carrier's entry is still waiting for its build response (nothing answered yet). */
export const awaitingResponse = (entry) => entry?.status === "sent" && !entry?.responseCode;

/** Declare interest: only while the entry is sent and unanswered. */
export const canDeclareInterest = awaitingResponse;

/** Decline: while sent or interested (an interested party can still decline); never twice. */
export const canDecline = (entry) => !isDeclined(entry) && (awaitingResponse(entry) || isInterested(entry));

// A carrier has one open quote request at a time
export const OPEN_REQUEST_STATUSES = ["sent", "quoted"];

/**
 * Leads the broker can send a quote request to: every lead that has declared
 * interest. A carrier can be sent more than one request on a submission.
 */
export const eligibleForQuoteRequest = (leads) => (leads || []).filter(isInterested);

/** How many quote requests each party has been sent: { partyId: count }. */
export const requestCounts = (requests) =>
  (requests || []).reduce((counts, r) => {
    const partyId = r.partyId || r.receiver?.partyId;
    if (partyId) counts[partyId] = (counts[partyId] || 0) + 1;
    return counts;
  }, {});

// How far a quote request has got, for picking a carrier's furthest-along one
const REQUEST_PROGRESS = { nominated: 5, quoted: 4, sent: 3, declined: 2, ntu: 1, withdrawn: 0 };

/** A carrier's furthest-along quote request (nominated, then quoted, sent, …), or undefined. */
export const furthestRequest = (requests) =>
  [...(requests || [])].sort(
    (a, b) =>
      (REQUEST_PROGRESS[b.status] ?? -1) - (REQUEST_PROGRESS[a.status] ?? -1) ||
      new Date(b.createdDate?.$date ?? b.createdDate ?? 0) - new Date(a.createdDate?.$date ?? a.createdDate ?? 0)
  )[0];

/** Notes on any placing record: free text up to 20,000 characters. */
export const MAX_NOTES = 20000;

/** A date from the API (ISO string or { $date }) as local time, or "". */
export const formatDate = (value) => {
  const raw = value?.$date ?? value;
  if (!raw) return "";
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? String(raw) : date.toLocaleString();
};

/** A datetime-local input value as an ISO 8601 UTC string, or undefined. */
export const toIsoUtc = (localValue) =>
  localValue ? new Date(localValue).toISOString() : undefined;

/** An API date as a datetime-local input value (local time), or "". */
export const toLocalInput = (value) => {
  const raw = value?.$date ?? value;
  if (!raw) return "";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

export const isInFuture = (localValue) =>
  !!localValue && new Date(localValue).getTime() > Date.now();

/**
 * Share terms of a quote request or quote. Records keep them under
 * `participation`; the quote comparison rows have them at the top level.
 */
export const readParticipation = (record) => {
  const p = record?.participation || {};
  const pick = (field) => p[field] ?? record?.[field];
  return {
    requestedShare: pick("requestedShare"),
    quotedShare: pick("quotedShare"),
    minimumShare: pick("minimumShare"),
    maximumShare: pick("maximumShare"),
    participationBasis: pick("participationBasis"),
    currency: pick("currency"),
    writtenShare: pick("writtenShare"),
    signedShare: pick("signedShare"),
    lineToStandIndicator: pick("lineToStandIndicator"),
  };
};

/** "10–30%" for a share range, or "" when neither end is set. */
export const formatShareRange = (min, max) =>
  min != null || max != null ? `${min ?? "—"}–${max ?? "—"}%` : "";

export const isPast = (value) => {
  const raw = value?.$date ?? value;
  return !!raw && new Date(raw).getTime() < Date.now();
};

const toNumber = (value) => (value === "" || value == null ? null : Number(value));

/**
 * Validates shares as percentages of the whole risk (more than 0, at most 100)
 * with min <= share <= max. `share` is the requested or quoted share.
 * Returns an error message, or null when valid.
 */
export const validateShares = ({ share, minimumShare, maximumShare }, { shareRequired = true } = {}) => {
  const values = {
    Share: toNumber(share),
    "Minimum share": toNumber(minimumShare),
    "Maximum share": toNumber(maximumShare),
  };
  for (const [label, v] of Object.entries(values)) {
    if (v == null) continue;
    if (Number.isNaN(v) || v <= 0 || v > 100) return `${label} must be more than 0 and at most 100.`;
  }
  const s = values.Share;
  const min = values["Minimum share"];
  const max = values["Maximum share"];
  if (shareRequired && s == null) return "Share is required.";
  if (s == null && (min == null || max == null)) return "Give a share, or a minimum and maximum share.";
  if (min != null && max != null && min > max) return "Minimum share can't be more than the maximum.";
  if (s != null && min != null && s < min) return "Share can't be less than the minimum.";
  if (s != null && max != null && s > max) return "Share can't be more than the maximum.";
  return null;
};

/** Number fields for the API: numbers, or omitted when blank. */
export const shareFields = (fields) =>
  Object.fromEntries(
    Object.entries(fields)
      .map(([k, v]) => [k, toNumber(v)])
      .filter(([, v]) => v != null)
  );
